import "./ThumbnailTrack.css";
import { Button } from "@ui/Button.jsx";
import { Stack } from "@ui/Stack.jsx";
import { Text } from "@ui/typography/Text.jsx";
import { createEffect, For, onCleanup, onMount } from "solid-js";

export type ThumbnailTrackItem = {
	url: string;
	/** Intrinsic pixel size, when known, so space is reserved before the image loads. */
	width?: number;
	height?: number;
	/** Full accessible name for the thumbnail button. Defaults to `"<itemLabel> N of M"`. */
	ariaLabel?: string;
};

export type ThumbnailTrackHandle = {
	/** Thumbnails fully inside the scroller, for page-sized keyboard steps. */
	visibleCount: () => number;
	/** Centres the thumbnail at `index` in the scroller. */
	scrollToIndex: (index: number) => void;
	/**
	 * Carousel keyboard shortcuts (Arrow, Home, End, PageUp, PageDown). The track
	 * takes no tab stop of its own, so attach this to whichever focusable region
	 * wraps it; that keeps the whole carousel a single tab stop.
	 */
	onKeyDown: (event: KeyboardEvent) => void;
};

type Props = {
	items: ThumbnailTrackItem[];
	/** Controlled selection - the parent owns the index. */
	selectedIndex: number;
	onSelect: (index: number) => void;
	/**
	 * Deliberate activation of an item: a click, or Enter/Space on a focused
	 * thumbnail. Unlike `onSelect` this does not fire for arrow navigation, so
	 * merely moving along the strip cannot trigger something like opening a pane.
	 */
	onActivate?: (index: number) => void;
	/** Noun used in accessible names, e.g. "Slide 3 of 7" or "Page 3 of 7". */
	itemLabel?: string;
	/**
	 * CSS length overriding the thumbnail height. Portrait items need more height
	 * than the landscape default to reach a comparable width.
	 */
	thumbnailHeight?: string;
	/**
	 * Politely announced when the selection changes, overriding the default
	 * `"<itemLabel> N of M"`. Use it to add whatever names the selected item.
	 */
	announcement?: string;
	handle?: (handle: ThumbnailTrackHandle) => void;
};

/**
 * Horizontal strip of selectable thumbnails: prev/next arrows (hold-to-repeat),
 * edge fade, scroll-into-view, roving tabindex so the strip is one tab stop, and
 * a polite live region announcing the selection.
 *
 * Selection is controlled, and the track renders nothing above the strip, so the
 * caller supplies whatever main view the thumbnails page through. Used by
 * {@link SlideShow} for deck slides and by the chat `DocumentView` for document
 * pages.
 */
export const ThumbnailTrack = (props: Props) => {
	let scrollRef!: HTMLElement;
	let timer: number | undefined;

	const itemLabel = () => props.itemLabel ?? "Slide";

	createEffect(() => {
		const n = props.items.length;
		if (props.selectedIndex >= n) {
			props.onSelect(Math.max(0, n - 1));
		}
	});

	const thumbnailButtonLabel = (idx: number) => {
		const explicit = props.items[idx]?.ariaLabel?.trim() ?? "";
		return explicit.length > 0
			? explicit
			: `${itemLabel()} ${idx + 1} of ${props.items.length}`;
	};

	const announcement = () =>
		props.announcement ??
		`${itemLabel()} ${props.selectedIndex + 1} of ${props.items.length}`;

	const maxFade = () =>
		parseFloat(getComputedStyle(document.documentElement).fontSize);

	const updateScrollState = () => {
		if (!scrollRef) return;
		const left = scrollRef.scrollLeft;
		const right = scrollRef.scrollWidth - scrollRef.clientWidth - left;
		const max = maxFade();
		scrollRef.style.setProperty("--fade-left", `${Math.min(left, max)}px`);
		scrollRef.style.setProperty("--fade-right", `${Math.min(right, max)}px`);
	};

	const clearTimer = () => {
		if (timer) {
			clearTimeout(timer);
			clearInterval(timer);
			timer = undefined;
		}
	};

	const scrollToCenter = (el: HTMLElement) => {
		const elRect = el.getBoundingClientRect();
		const containerRect = scrollRef.getBoundingClientRect();
		const elCenter = elRect.left + elRect.width / 2;
		const containerCenter = containerRect.left + containerRect.width / 2;
		scrollRef.scrollBy({
			left: elCenter - containerCenter,
			behavior: "smooth",
		});
	};

	const scrollToThumbnail = (el: HTMLElement) => {
		if (!scrollRef) return;
		scrollToCenter(el);
	};

	const scrollToIndex = (index: number) => {
		const thumbs = scrollRef?.querySelectorAll<HTMLElement>(".slide-thumbnail");
		const thumb = thumbs?.[index];
		if (thumb) scrollToThumbnail(thumb);
	};

	/** Keep roving tabindex in sync when the active item changes while focus stays in the strip. */
	createEffect(() => {
		const idx = props.selectedIndex;
		queueMicrotask(() => {
			if (!scrollRef) return;
			const activeEl = document.activeElement;
			if (!(activeEl instanceof HTMLElement)) return;
			if (!scrollRef.contains(activeEl)) return;
			if (!activeEl.classList.contains("slide-thumbnail-button")) return;
			const focusedIdx = Number.parseInt(activeEl.dataset.thumbIndex ?? "", 10);
			if (Number.isFinite(focusedIdx) && focusedIdx === idx) return;
			const next = scrollRef.querySelector<HTMLElement>(
				`.slide-thumbnail-button[data-thumb-index="${idx}"]`,
			);
			next?.focus({ preventScroll: true });
			if (next) scrollToThumbnail(next);
		});
	});

	const selectItem = (index: number) => {
		const clamped = Math.max(0, Math.min(index, props.items.length - 1));
		props.onSelect(clamped);
		scrollToIndex(clamped);
	};

	const stepFromArrow = (direction: "left" | "right") => {
		selectItem(props.selectedIndex + (direction === "left" ? -1 : 1));
	};

	let lastPointerActivateLeft = 0;
	let lastPointerActivateRight = 0;

	const CLICK_AFTER_POINTER_GUARD_MS = 500;

	let holdPointerAbort: AbortController | undefined;

	const startHoldArrowRepeat = (
		e: Pick<PointerEvent, "pointerType" | "button">,
		direction: "left" | "right",
	) => {
		if (e.pointerType === "mouse" && e.button !== 0) {
			return;
		}
		clearTimer();
		holdPointerAbort?.abort();
		holdPointerAbort = new AbortController();
		const { signal } = holdPointerAbort;
		const now = Date.now();
		if (direction === "left") {
			lastPointerActivateLeft = now;
		} else {
			lastPointerActivateRight = now;
		}

		const endRepeat = () => {
			clearTimer();
		};

		window.addEventListener("pointerup", endRepeat, { capture: true, signal });
		window.addEventListener("pointercancel", endRepeat, {
			capture: true,
			signal,
		});

		stepFromArrow(direction);

		const delay = 400;
		const interval = 200;
		timer = window.setTimeout(() => {
			timer = window.setInterval(() => {
				stepFromArrow(direction);
			}, interval);
		}, delay);
	};

	const visibleCount = () => {
		if (!scrollRef) return 1;
		const thumbnails =
			scrollRef.querySelectorAll<HTMLElement>(".slide-thumbnail");
		if (thumbnails.length === 0) return 1;
		const scrollRect = scrollRef.getBoundingClientRect();
		let count = 0;
		for (const thumb of thumbnails) {
			const rect = thumb.getBoundingClientRect();
			if (
				rect.left >= scrollRect.left - 1 &&
				rect.right <= scrollRect.right + 1
			) {
				count++;
			}
		}
		return Math.max(count, 1);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === "ArrowLeft") {
			e.preventDefault();
			selectItem(props.selectedIndex - 1);
		} else if (e.key === "ArrowRight") {
			e.preventDefault();
			selectItem(props.selectedIndex + 1);
		} else if (e.key === "Home") {
			e.preventDefault();
			selectItem(0);
		} else if (e.key === "End") {
			e.preventDefault();
			selectItem(props.items.length - 1);
		} else if (e.key === "PageUp") {
			e.preventDefault();
			selectItem(props.selectedIndex - visibleCount() + 1);
		} else if (e.key === "PageDown") {
			e.preventDefault();
			selectItem(props.selectedIndex + visibleCount() - 1);
		}
	};

	onMount(() => {
		props.handle?.({ visibleCount, scrollToIndex, onKeyDown });
		updateScrollState();
		scrollRef.addEventListener("scroll", updateScrollState, { passive: true });
		const observer = new ResizeObserver(updateScrollState);
		observer.observe(scrollRef);
		onCleanup(() => {
			clearTimer();
			holdPointerAbort?.abort();
			scrollRef.removeEventListener("scroll", updateScrollState);
			observer.disconnect();
		});
	});

	return (
		<div
			class="slides-container"
			style={
				props.thumbnailHeight !== undefined
					? { "--thumbnail-height": props.thumbnailHeight }
					: undefined
			}
		>
			<p
				class="thumbnail-track-announcement"
				aria-live="polite"
				aria-atomic="true"
			>
				{announcement()}
			</p>
			<Button
				type="button"
				variant="text"
				class="slides-arrow"
				symbol="chevron_left"
				symbolRelativeSize="even-larger"
				aria-label={`Previous ${itemLabel().toLowerCase()}`}
				disabled={props.selectedIndex <= 0}
				onPointerDown={(e) => startHoldArrowRepeat(e, "left")}
				onClick={() => {
					if (
						Date.now() - lastPointerActivateLeft <
						CLICK_AFTER_POINTER_GUARD_MS
					) {
						return;
					}
					clearTimer();
					holdPointerAbort?.abort();
					stepFromArrow("left");
				}}
			/>
			<section
				class="slides-scroll"
				ref={scrollRef}
				aria-label={`${itemLabel()} thumbnails`}
			>
				<Stack class="slide-thumbnails" direction="row" spacing="small">
					<For each={props.items}>
						{(item, index) => (
							<button
								type="button"
								class="slide-thumbnail-button"
								classList={{ active: index() === props.selectedIndex }}
								data-thumb-index={index()}
								tabIndex={index() === props.selectedIndex ? 0 : -1}
								aria-label={thumbnailButtonLabel(index())}
								aria-current={
									index() === props.selectedIndex ? "true" : undefined
								}
								onClick={(e) => {
									e.preventDefault();
									props.onSelect(index());
									props.onActivate?.(index());
									const img = e.currentTarget.querySelector("img");
									if (img) scrollToThumbnail(img);
								}}
							>
								<img
									class="slide-thumbnail"
									src={item.url}
									width={item.width}
									height={item.height}
									alt=""
									loading="lazy"
									decoding="async"
								/>
							</button>
						)}
					</For>
				</Stack>
			</section>

			<Button
				type="button"
				class="slides-arrow"
				variant="text"
				symbol="chevron_right"
				symbolRelativeSize="even-larger"
				aria-label={`Next ${itemLabel().toLowerCase()}`}
				disabled={props.selectedIndex >= props.items.length - 1}
				onPointerDown={(e) => startHoldArrowRepeat(e, "right")}
				onClick={() => {
					if (
						Date.now() - lastPointerActivateRight <
						CLICK_AFTER_POINTER_GUARD_MS
					) {
						return;
					}
					clearTimer();
					holdPointerAbort?.abort();
					stepFromArrow("right");
				}}
			/>
		</div>
	);
};

type CaptionProps = {
	/** Left-hand title: a deck's slide headline, or a document's title. */
	title?: string;
	selectedIndex: number;
	total: number;
};

/**
 * Caption row for a {@link ThumbnailTrack}: a title on the left, and the
 * position of the selected item on the right with the current number picked
 * out. Callers render it directly above their track, so every paged preview
 * captions its strip the same way.
 *
 * The position is decorative - the thumbnail buttons already carry it in their
 * accessible names - so it is hidden from assistive tech.
 */
export const ThumbnailTrackCaption = (props: CaptionProps) => (
	<Stack direction="row" align="center" justify="space-between" spacing="large">
		<Text size="intermediate" weight="bold" color="ink">
			{props.title}
		</Text>
		{/* Never shrink, or a wide title wraps the position at its space. */}
		<div aria-hidden="true" style={{ "flex-shrink": 0 }}>
			<Text family="mono" size="small" weight="semi-bold" color="fg3">
				<Text as="span" family="mono" color="primary" weight="bold">
					{props.selectedIndex + 1}
				</Text>{" "}
				/ {props.total}
			</Text>
		</div>
	</Stack>
);
